import React, { useState } from 'react'
import { Box, Button, FormField, Text, TextInput, CheckBox, Heading, Table, TableHeader, TableRow, TableCell, TableBody } from 'grommet'
import { FormAdd, FormClose, FormUpload } from 'grommet-icons'
import { PacmanLoader } from 'react-spinners'
import qlikBrand from '../../branding'

const allLang = ['de-DE', 'es-ES', 'fr-FR', 'it-IT', 'ja-JP', 'ko-KR', 'nl-NL', 'pl-PL', 'pt-BR', 'ru-RU', 'sv-SE', 'tr-TR', 'zh-CN', 'zh-TW']

// Sanitization helper
const sanitizeText = (text) => {
  if (!text) return ''
  // Remove potentially dangerous characters and scripts
  return text
    .replace(/[<>]/g, '') // Remove angle brackets to prevent HTML injection
    .replace(/[{}]/g, '') // Remove curly braces
    .replace(/[\[\]]/g, '') // Remove square brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers like onclick=
}

// Validation helpers
const validateJiraId = (value) => {
  if (!value) return 'Required'
  const jiraMatcher = /\d+-[A-Z]+(?!-?[a-zA-Z]{1,10})/g
  const reversed = value.split('').reverse().join('')
  if (!reversed.match(jiraMatcher)) return 'Incorrect format'
  return null
}

function CreateBugfixFormReact({
  onSubmit,
  repositories,
  repoStringsSuggestions,
  repoSuggestions,
  stringValues,
  fetchRepoStrings,
  fetchRepoStringsInProgress,
  fetchStringValue,
  addBugFieldStringValues,
  addStringFieldStringValues,
  removeBugFieldStringValues,
  removeStringFieldStringValues
}) {
  const [formData, setFormData] = useState({
    name: '',
    bugs: [{
      jiraId: '',
      resources: [{
        component: '',
        resource: '',
        languages: {},
        description: '',
        translationProvided: false
      }]
    }]
  })
  
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Add bug
  const addBug = () => {
    const newBugs = [...formData.bugs, {
      jiraId: '',
      resources: [{
        component: '',
        resource: '',
        languages: {},
        description: '',
        translationProvided: false
      }]
    }]
    setFormData({ ...formData, bugs: newBugs })
    addBugFieldStringValues()
  }

  // Remove bug
  const removeBug = (index) => {
    const newBugs = formData.bugs.filter((_, i) => i !== index)
    setFormData({ ...formData, bugs: newBugs })
    removeBugFieldStringValues(index)
  }

  // Add resource to bug
  const addResource = (bugIndex) => {
    const newBugs = [...formData.bugs]
    newBugs[bugIndex].resources.push({
      component: '',
      resource: '',
      languages: {},
      description: '',
      translationProvided: false
    })
    setFormData({ ...formData, bugs: newBugs })
    addStringFieldStringValues(bugIndex)
  }

  // Remove resource from bug
  const removeResource = (bugIndex, resourceIndex) => {
    const newBugs = [...formData.bugs]
    newBugs[bugIndex].resources = newBugs[bugIndex].resources.filter((_, i) => i !== resourceIndex)
    setFormData({ ...formData, bugs: newBugs })
    removeStringFieldStringValues(bugIndex, resourceIndex)
  }

  // Update bug field
  const updateBugField = (bugIndex, field, value) => {
    const newBugs = [...formData.bugs]
    newBugs[bugIndex][field] = value
    setFormData({ ...formData, bugs: newBugs })
  }

  // Update resource field
  const updateResourceField = (bugIndex, resourceIndex, field, value) => {
    const newBugs = [...formData.bugs]
    newBugs[bugIndex].resources[resourceIndex][field] = value
    setFormData({ ...formData, bugs: newBugs })
  }

  // Update language checkbox
  const updateLanguage = (bugIndex, resourceIndex, lang, checked) => {
    const newBugs = [...formData.bugs]
    newBugs[bugIndex].resources[resourceIndex].languages[lang] = checked
    setFormData({ ...formData, bugs: newBugs })
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}
    
    // Validate project name
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Project name is required'
    }
    
    formData.bugs.forEach((bug, bugIndex) => {
      const jiraError = validateJiraId(bug.jiraId)
      if (jiraError) {
        newErrors[`bugs[${bugIndex}].jiraId`] = jiraError
      }
      
      bug.resources.forEach((resource, resourceIndex) => {
        if (!resource.component) {
          newErrors[`bugs[${bugIndex}].resources[${resourceIndex}].component`] = 'Required'
        }
        if (!resource.resource) {
          newErrors[`bugs[${bugIndex}].resources[${resourceIndex}].resource`] = 'Required'
        } else {
          // Check if the entered resource exists in the suggestions
          const availableResources = repoStringsSuggestions[bugIndex]?.[resourceIndex] || []
          const resourceExists = availableResources.some(r => r.name === resource.resource)
          if (availableResources.length > 0 && !resourceExists) {
            newErrors[`bugs[${bugIndex}].resources[${resourceIndex}].resource`] = 'Resource not found. Please select from the list.'
          }
        }
        
        const hasLanguage = Object.values(resource.languages).some(v => v)
        if (!hasLanguage) {
          newErrors[`bugs[${bugIndex}].resources[${resourceIndex}].languages`] = 'At least one language is required'
        }
      })
    })
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validateForm()) {
      setIsSubmitting(true)
      try {
        await onSubmit(formData)
      } catch (error) {
        console.error('Submit error:', error)
        setIsSubmitting(false)
      }
      // Note: Don't set isSubmitting to false here if navigation happens after onSubmit
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box gap='medium' style={{ opacity: isSubmitting ? 0.6 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}>
        {/* Project Name */}
        <FormField label='Project Name' error={errors.name}>
          <TextInput
            placeholder='Enter project name'
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: sanitizeText(e.target.value) })}
            disabled={isSubmitting}
          />
        </FormField>

        {/* Bugs */}
        <Box gap='small'>
          <Heading level='3'>Bugs</Heading>
          {formData.bugs.map((bug, bugIndex) => (
            <Box key={bugIndex} border round pad='small' gap='small'>
              <Box align='end'>
                <Button
                  icon={<FormClose color='status-critical' />}
                  onClick={() => removeBug(bugIndex)}
                  disabled={formData.bugs.length === 1}
                />
              </Box>

              {/* Jira ID */}
              <FormField
                label='Jira ID'
                error={errors[`bugs[${bugIndex}].jiraId`]}
              >
                <TextInput
                  placeholder='e.g., RDL-123'
                  value={bug.jiraId}
                  onChange={(e) => updateBugField(bugIndex, 'jiraId', e.target.value)}
                />
              </FormField>

              {/* Resources */}
              <Box gap='small' margin={{ top: 'small' }}>
                <Heading level='4'>Resources</Heading>
                {bug.resources.map((resource, resourceIndex) => (
                  <Box key={resourceIndex} border round pad='small' gap='small' background='light-2'>
                    <Box align='end'>
                      <Button
                        icon={<FormClose color='status-critical' />}
                        onClick={() => removeResource(bugIndex, resourceIndex)}
                        disabled={bug.resources.length === 1}
                      />
                    </Box>

                    {/* Component Autocomplete */}
                    <FormField
                      label='Component'
                      error={errors[`bugs[${bugIndex}].resources[${resourceIndex}].component`]}
                    >
                      <TextInput
                        placeholder='Enter Component'
                        value={resource.component}
                        onChange={(e) => {
                          const value = e.target.value
                          updateResourceField(bugIndex, resourceIndex, 'component', value)
                          // Clear the resource field when component changes
                          updateResourceField(bugIndex, resourceIndex, 'resource', '')
                          // Find matching repository
                          const matchingRepo = repositories.find(r => 
                            (r.description ? `${r.name} - ${r.description}` : r.name) === value
                          )
                          if (matchingRepo) {
                            fetchRepoStrings(matchingRepo, bugIndex, resourceIndex)
                          }
                        }}
                        list={`component-suggestions-${bugIndex}-${resourceIndex}`}
                      />
                      <datalist id={`component-suggestions-${bugIndex}-${resourceIndex}`}>
                        {repoSuggestions.map((suggestion, i) => (
                          <option key={i} value={suggestion} />
                        ))}
                      </datalist>
                    </FormField>

                    {/* Resource Autocomplete */}
                    <FormField
                      label='Resource'
                      error={errors[`bugs[${bugIndex}].resources[${resourceIndex}].resource`]}
                    >
                      {fetchRepoStringsInProgress ? (
                        <Box direction='row' align='center' gap='small' pad='small'>
                          <PacmanLoader loading={true} color={qlikBrand.core.green} size={15} />
                          <Text size='small'>Loading resources...</Text>
                        </Box>
                      ) : (
                        <>
                          <TextInput
                            placeholder='Enter Resource'
                            value={resource.resource}
                            onChange={(e) => {
                              const value = e.target.value
                              updateResourceField(bugIndex, resourceIndex, 'resource', value)
                              // Find matching resource by name
                              const componentName = resource.component
                              const resources = repoStringsSuggestions[bugIndex]?.[resourceIndex]
                              const matchingResource = resources?.find(r => r.name === value)
                              if (matchingResource) {
                                fetchStringValue(matchingResource._id, bugIndex, resourceIndex)
                              }
                            }}
                            list={`resource-suggestions-${bugIndex}-${resourceIndex}`}
                          />
                          <datalist id={`resource-suggestions-${bugIndex}-${resourceIndex}`}>
                            {(repoStringsSuggestions[bugIndex]?.[resourceIndex] || []).map((suggestion, i) => (
                              <option key={i} value={suggestion.name} />
                            ))}
                          </datalist>
                        </>
                      )}
                    </FormField>

                    {/* Languages Table */}
                    <Box>
                      <Heading level='5'>Languages</Heading>
                      {errors[`bugs[${bugIndex}].resources[${resourceIndex}].languages`] && (
                        <Text color='status-critical' size='small'>
                          {errors[`bugs[${bugIndex}].resources[${resourceIndex}].languages`]}
                        </Text>
                      )}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableCell scope='col' border='bottom'>Include</TableCell>
                            <TableCell scope='col' border='bottom'>Language</TableCell>
                            <TableCell scope='col' border='bottom'>Translation</TableCell>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {/* en-US row with resourceData.value */}
                          {(() => {
                            const resourceData = stringValues?.[bugIndex]?.[resourceIndex]
                            const enUsValue = resourceData?.value || ''
                            
                            return (
                              <TableRow key='en-US'>
                                <TableCell>
                                  {/* No checkbox for en-US */}
                                </TableCell>
                                <TableCell><strong>en-US</strong></TableCell>
                                <TableCell>
                                  <Text size='small'>{enUsValue}</Text>
                                </TableCell>
                              </TableRow>
                            )
                          })()}
                          
                          {allLang.map((lang) => {
                            // Get translation value from stringValues
                            const resourceData = stringValues?.[bugIndex]?.[resourceIndex]
                            const translation = resourceData?.translations?.find(t => t.locale === lang)
                            const translationValue = translation?.value || ''
                            
                            return (
                              <TableRow key={lang}>
                                <TableCell>
                                  <CheckBox
                                    checked={!!resource.languages[lang]}
                                    onChange={(e) => updateLanguage(bugIndex, resourceIndex, lang, e.target.checked)}
                                  />
                                </TableCell>
                                <TableCell>{lang}</TableCell>
                                <TableCell>
                                  <Text size='small'>{translationValue}</Text>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </Box>

                    {/* Description Field */}
                    <FormField label='Problem Description'>
                      <TextInput
                        placeholder='Enter problem description'
                        value={resource.description || ''}
                        onChange={(e) => updateResourceField(bugIndex, resourceIndex, 'description', sanitizeText(e.target.value))}
                      />
                    </FormField>

                    {/* Translation Provided Checkbox */}
                    <Box direction='row' align='center' gap='small' pad={{ vertical: 'small' }}>
                      <CheckBox
                        checked={!!resource.translationProvided}
                        onChange={(e) => updateResourceField(bugIndex, resourceIndex, 'translationProvided', e.target.checked)}
                      />
                      <Text>Translation Provided?</Text>
                    </Box>
                  </Box>
                ))}
                <Button
                  icon={<FormAdd />}
                  label='Add Resource'
                  onClick={() => addResource(bugIndex)}
                  alignSelf='start'
                />
              </Box>
            </Box>
          ))}
          <Button
            icon={<FormAdd />}
            label='Add Bug'
            onClick={addBug}
            alignSelf='start'
          />
        </Box>

        {/* Submit Button */}
        <Box direction='row' gap='medium' margin={{ top: 'medium' }} align='center'>
          <Button
            type='submit'
            primary
            icon={<FormUpload />}
            label='Submit Bugfix Project'
            disabled={isSubmitting}
          />
          {
            Object.keys(errors).length > 0 && (
            <Box direction='row' align='center' gap='small'>
              <Text size='medium' color='red'>Oops, there are some errors</Text>
            </Box>
          )}
          {isSubmitting && (
            <Box direction='row' align='center' gap='small'>
              <PacmanLoader loading={true} color={qlikBrand.core.green} size={25} />
              <Text size='medium'>Submitting...</Text>
            </Box>
          )}
        </Box>
      </Box>
    </form>
  )
}

export default CreateBugfixFormReact
